/**
 * useVoiceStore — Zustand store for voice conversation preferences.
 *
 * Stores two categories of state:
 *   1. **Workspace-level** (`provider`, `profileId`, `resolvedProfile`, `selectedVoice`) —
 *      resolved from `platform-runtime.voiceProfileId` and org defaults.
 *      The active voice is owned by the platform runtime profile.
 *   2. **User-level** (`volume`, `audioOutputEnabled`) — persisted in
 *      localStorage per user, independent of workspace.
 *
 * DevPlan 0053 — Task 3.c
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { VoiceProviderType } from '@/hooks/useVoiceConversation';
import { usePlatformSettingsStore } from '@/stores/usePlatformSettingsStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  type VoiceRuntimeProfile,
  getDefaultVoiceProfile,
  getVoiceProviderFromProfile,
  parseVoiceProfiles,
  parseWorkspaceVoiceProfileId,
} from '@/app/features/settings/platform/runtimeConfig';

// ── Provider availability ─────────────────────────────────────────────────

export type ElevenLabsMode = 'tts';

export interface VoiceProviderInfo {
  id: VoiceProviderType;
  name: string;
  available: boolean;
  reason?: string;
  /** ElevenLabs only: 'tts' (default) or 'agent' (ConvAI). */
  mode?: ElevenLabsMode;
  profile_id?: string | null;
  voice_id?: string | null;
}

function getDefaultSelectedVoice(
  provider: VoiceProviderType,
  profile?: VoiceRuntimeProfile,
): string {
  const curatedVoice = profile?.voiceIds?.find(
    (voiceId) => typeof voiceId === 'string' && voiceId.trim().length > 0,
  );

  if (curatedVoice) {
    return curatedVoice;
  }

  return provider === 'azure-realtime' ? 'alloy' : '';
}

// ── Store shape ───────────────────────────────────────────────────────────

interface VoiceState {
  /** Active voice provider for the workspace (loaded from server). */
  provider: VoiceProviderType;
  /** Selected workspace/runtime profile id. */
  profileId?: string;
  /** Resolved voice runtime profile for the active workspace. */
  resolvedProfile?: VoiceRuntimeProfile;
  /** Human-readable resolution issue for missing/invalid profiles. */
  runtimeIssue: string | null;
  /** ElevenLabs mode reported by backend. */
  elevenlabsMode: ElevenLabsMode;
  /** Whether spoken-answer output is available for the active workspace. */
  ttsAvailable: boolean;
  /** Active ElevenLabs TTS voice id derived from the TTS profile. */
  ttsVoiceId: string;
  /** Active voice id derived from the resolved runtime profile. */
  selectedVoice: string;
  /** Playback volume 0–1. */
  volume: number;
  /** Whether TTS audio output is enabled for voice sessions. */
  audioOutputEnabled: boolean;
  /** Available providers (fetched from backend). */
  availableProviders: VoiceProviderInfo[];
  /** Loading state for async fetch operations. */
  loading: boolean;

  // ── Actions ────────────────────────────────────────────────────────────

  setProvider: (provider: VoiceProviderType) => void;
  setSelectedVoice: (voice: string) => void;
  setVolume: (volume: number) => void;
  setAudioOutputEnabled: (enabled: boolean) => void;
  setAvailableProviders: (providers: VoiceProviderInfo[]) => void;

  /**
   * Fetch voice settings from workspace plugin settings + backend
   * provider availability. Call on workspace switch.
   */
  fetchVoiceSettings: (workspaceId: string, token?: string | null) => Promise<void>;

  /**
   * Fetch available providers from the Python backend.
   */
  fetchProviders: () => Promise<void>;
}

function parseSettingsJson(raw: unknown): Record<string, unknown> | undefined {
  if (!raw) {
    return undefined;
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : undefined;
    } catch {
      return undefined;
    }
  }

  return typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : undefined;
}

function getFirstAvailableVoiceProfile(
  profiles: VoiceRuntimeProfile[],
  providers: VoiceProviderInfo[],
): VoiceRuntimeProfile | undefined {
  const availability = new Map(providers.map((provider) => [provider.id, provider.available]));

  return profiles.find((profile) => {
    const providerId = getVoiceProviderFromProfile(profile);
    return providerId ? availability.get(providerId) === true : false;
  });
}

export const useVoiceStore = create<VoiceState>()(
  persist(
    (set) => ({
      // Defaults
      provider: 'azure-realtime',
      profileId: undefined,
      resolvedProfile: undefined,
      runtimeIssue: null,
      elevenlabsMode: 'tts',
      ttsAvailable: false,
      ttsVoiceId: '',
      selectedVoice: 'alloy',
      volume: 0.8,
      audioOutputEnabled: false,
      availableProviders: [],
      loading: false,

      setProvider: (provider) => {
        const current = useVoiceStore.getState();
        const matchingProfile = current.resolvedProfile
          && getVoiceProviderFromProfile(current.resolvedProfile) === provider
          ? current.resolvedProfile
          : undefined;
        const nextVoice = getDefaultSelectedVoice(provider, matchingProfile);
        if (current.provider !== provider) {
          set({
            provider,
            selectedVoice: nextVoice,
          });
        } else {
          set({ provider, selectedVoice: nextVoice });
        }
      },
      setSelectedVoice: (voice) => set({ selectedVoice: voice }),
      setVolume: (volume) => set({ volume: Math.max(0, Math.min(1, volume)) }),
      setAudioOutputEnabled: (enabled) => set({ audioOutputEnabled: enabled }),
      setAvailableProviders: (providers) => set({ availableProviders: providers }),

      fetchVoiceSettings: async (workspaceId, token) => {
        set({ loading: true });
        try {
          const platformSettingsState = usePlatformSettingsStore.getState();
          if (!platformSettingsState.loaded) {
            await platformSettingsState.fetchSettings(token);
          }

          const platformSettings = usePlatformSettingsStore.getState().settings;
          const voiceProfiles = parseVoiceProfiles(
            platformSettings.find((setting) => setting.key === 'ai.voice_profiles')?.value,
          );
          const defaultVoiceProfileId =
            platformSettings.find((setting) => setting.key === 'ai.voice_default_profile')?.value?.trim()
            || undefined;
          const defaultVoiceProfile = getDefaultVoiceProfile(voiceProfiles, defaultVoiceProfileId);

          // 1. Read workspace plugin settings
          const headers: HeadersInit = {};
          if (token) headers.Authorization = `Bearer ${token}`;
          let persistedProfileId: string | undefined;

          const res = await fetch(
            `/api/dotnet/workspaces/${workspaceId}/plugins/settings`,
            { headers },
          );
          if (res.ok) {
            const raw = (await res.json()) as Array<{
              pluginId: string;
              enabled: boolean;
              settingsJson?: unknown;
            }>;
            const settings = Array.isArray(raw) ? raw : [];
            const runtimeSettings = parseSettingsJson(
              settings.find((setting) => setting.pluginId === 'platform-runtime')?.settingsJson,
            );
            persistedProfileId = parseWorkspaceVoiceProfileId(runtimeSettings);
            const explicitProfile = persistedProfileId
              ? voiceProfiles.find((profile) => profile.id === persistedProfileId)
              : undefined;
            const resolvedProfile = explicitProfile
              ?? defaultVoiceProfile;
            const resolvedProvider = getVoiceProviderFromProfile(resolvedProfile);
            const runtimeIssue = persistedProfileId && !explicitProfile
              ? `Voice-Profil \"${persistedProfileId}\" ist nicht mehr verfügbar.`
              : !resolvedProfile && voiceProfiles.length === 0
                ? 'Es sind keine Voice-Profile in den Plattform-Settings definiert.'
                : null;

            const updates: Partial<VoiceState> = {
              profileId: resolvedProfile?.id,
              resolvedProfile,
              runtimeIssue,
            };

            if (resolvedProvider) {
              updates.provider = resolvedProvider;
              updates.selectedVoice = getDefaultSelectedVoice(resolvedProvider, resolvedProfile);
            }

            set(updates);
          }

          // 2. Fetch available providers from Python backend
          const providersUrl = workspaceId
            ? `/api/python/voice/providers?workspaceId=${encodeURIComponent(workspaceId)}`
            : '/api/python/voice/providers';
          const provRes = await fetch(providersUrl, { headers });
          if (provRes.ok) {
            const data = (await provRes.json()) as {
              providers: VoiceProviderInfo[];
            };
            if (Array.isArray(data.providers)) {
              const providerAvailability = new Map(
                data.providers.map((entry) => [entry.id, entry.available]),
              );

              let nextResolvedProfile = useVoiceStore.getState().resolvedProfile;
              let nextProfileId = useVoiceStore.getState().profileId;
              let nextRuntimeIssue = useVoiceStore.getState().runtimeIssue;
              const currentProvider = getVoiceProviderFromProfile(nextResolvedProfile);
              const currentProviderAvailable = currentProvider
                ? providerAvailability.get(currentProvider) === true
                : false;

              if (!persistedProfileId && voiceProfiles.length > 0 && !currentProviderAvailable) {
                const fallbackProfile = getFirstAvailableVoiceProfile(voiceProfiles, data.providers);
                if (fallbackProfile) {
                  nextResolvedProfile = fallbackProfile;
                  nextProfileId = fallbackProfile.id;
                  nextRuntimeIssue = null;
                }
              }

              const nextProvider = getVoiceProviderFromProfile(nextResolvedProfile);
              const updates: Partial<VoiceState> = {
                availableProviders: data.providers,
                profileId: nextProfileId,
                resolvedProfile: nextResolvedProfile,
                runtimeIssue: nextRuntimeIssue,
              };

              if (nextProvider) {
                updates.provider = nextProvider;
                updates.selectedVoice = getDefaultSelectedVoice(nextProvider, nextResolvedProfile);
              }

              set(updates);
              // Extract ElevenLabs TTS info
              const elProvider = data.providers.find((p: VoiceProviderInfo) => p.id === 'elevenlabs');
              if (elProvider) {
                set({
                  elevenlabsMode: 'tts',
                  ttsAvailable: elProvider.available,
                  ttsVoiceId: elProvider.voice_id ?? '',
                  audioOutputEnabled: elProvider.available ? useVoiceStore.getState().audioOutputEnabled : false,
                });
              } else {
                set({ ttsAvailable: false, ttsVoiceId: '', audioOutputEnabled: false });
              }
            }
          }
        } catch {
          // Voice settings are optional — fail silently
        } finally {
          set({ loading: false });
        }
      },

      fetchProviders: async () => {
        try {
          const workspaceId = useWorkspaceStore.getState().activeWorkspace?.id;
          const providersUrl = workspaceId
            ? `/api/python/voice/providers?workspaceId=${encodeURIComponent(workspaceId)}`
            : '/api/python/voice/providers';
          const res = await fetch(providersUrl);
          if (res.ok) {
            const data = (await res.json()) as {
              providers: VoiceProviderInfo[];
            };
            if (Array.isArray(data.providers)) {
              set({ availableProviders: data.providers });
              const elProvider = data.providers.find((p: VoiceProviderInfo) => p.id === 'elevenlabs');
              if (elProvider) {
                set({
                  elevenlabsMode: 'tts',
                  ttsAvailable: elProvider.available,
                  ttsVoiceId: elProvider.voice_id ?? '',
                  audioOutputEnabled: elProvider.available ? useVoiceStore.getState().audioOutputEnabled : false,
                });
              } else {
                set({ ttsAvailable: false, ttsVoiceId: '', audioOutputEnabled: false });
              }
            }
          }
        } catch {
          // fail silently
        }
      },
    }),
    {
      name: 'sopho-voice-store',
      version: 2,
      migrate: (persistedState) => {
        const persisted = persistedState as { volume?: unknown };
        return {
          volume: typeof persisted.volume === 'number' ? persisted.volume : 0.8,
          audioOutputEnabled: false,
        };
      },
      // Only persist user-level preferences. Voice choice stays platform-owned.
      partialize: (state) => ({
        volume: state.volume,
        audioOutputEnabled: state.audioOutputEnabled,
      }),
    },
  ),
);
